import { HSComponent } from "./component.js";
import * as Logger from "./logger.js";
import * as Utils from "./utils.js";
import styles from "./snapshot-picker.css";
import template from "./snapshot-picker.html";
import { UserSession } from './session.js';

export class HSSnapshotPicker extends HSComponent {
    currentAnalysisWindow = 'month';
    userLocaleWeekStart = 0;

    constructor() {
        super();
    }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
    }

    init({ analysisWindow = 'month', userLocaleWeekStart = Utils.getLocaleWeekStart() } = {}) {
        Logger.debug("HSSnapshotPicker init: ", { analysisWindow, userLocaleWeekStart });
        
        this.currentAnalysisWindow = analysisWindow;
        this.userLocaleWeekStart = userLocaleWeekStart;

        const select = this.shadowRoot.querySelector('.snapshot-select');
        if (!select) {
            Logger.error("Snapshot select not found");
            return;
        }

        // Generate snapshot options based on analysis window
        this.updateSnapshotOptions(analysisWindow, userLocaleWeekStart);

        // Listen for selection changes
        select.onchange = (e) => {
            const [ rawTs ] = String(e.target.value || '').split('|');
            const selectedTimestamp = Number(rawTs);
            const selectedOpt = e.target.selectedOptions?.[0] || null;
            if (Number.isFinite(selectedTimestamp) && selectedOpt) {
                const meta = {
                    scope: analysisWindow,
                    anchorType: String(selectedOpt.dataset.anchorType || 'calendar'),
                    isCurrentPeriod: String(selectedOpt.dataset.isCurrent || '') === 'true',
                    label: String(selectedOpt.textContent || '').trim(),
                };
                Logger.debug("Snapshot selected: ", { selectedTimestamp, meta });
                UserSession.set('statsSnapshotAnchor', selectedTimestamp);
                UserSession.set('statsSnapshotAnchorSelection', String(e.target.value || ''));
                UserSession.set('statsSnapshotAnchorMeta', meta);

                // Emit event for card to reload data
                this.emit({
                    type: 'hs-snapshot-changed',
                    timestamp: selectedTimestamp,
                    analysisWindow,
                    ...meta,
                });
            }
        };
    }

    async updateSnapshotOptions(analysisWindow, userLocaleWeekStart, { forceLatest = false } = {}) {
        Logger.debug("Updating snapshot options for window: ", analysisWindow);
        
        const select = this.shadowRoot.querySelector('.snapshot-select');
        const selectorEl = this.shadowRoot.querySelector('.snapshot-selector');
        if (!select || !selectorEl) return;

        // The "all" scope has no meaningful snapshot anchor — disable the picker.
        if (analysisWindow === 'all') {
            select.disabled = true;
            UserSession.set('statsSnapshotAnchor', null);
            this.emit({
                type: 'hs-snapshot-changed',
                timestamp: null,
                analysisWindow
            });
            return;
        }

        // Re-enable in case we're switching away from 'all'.
        select.disabled = false;

        // Generate new options
        const options = Utils.generateSnapshotOptions(analysisWindow, userLocaleWeekStart);
        const now = new Date();
        const todayStartUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);

        const grouped = this.buildGroupedOptions(analysisWindow, options, todayStartUtc);
        const allOptions = [ ...grouped.rolling, ...grouped.calendar ];
        
        Logger.debug("Generated snapshot options: ", options);

        // Rebuild option list with grouped sections.
        select.innerHTML = '';

        const buildValue = (item) => `${Number(item.timestamp)}|${String(item.anchorType || 'calendar')}`;
        const appendGroup = (label, items) => {
            if (!Array.isArray(items) || items.length < 1) return;
            const group = document.createElement('optgroup');
            group.label = label;
            items.forEach((item) => {
                const optElement = document.createElement('option');
                optElement.value = buildValue(item);
                optElement.textContent = item.label;
                optElement.dataset.anchorType = String(item.anchorType || 'calendar');
                optElement.dataset.isCurrent = item.isCurrentPeriod ? 'true' : 'false';
                optElement.dataset.scope = analysisWindow;
                group.appendChild(optElement);
            });
            select.appendChild(group);
        };

        appendGroup('Rolling', grouped.rolling);
        appendGroup('Calendar', grouped.calendar);

        const currentAnchor = Number(UserSession.get('statsSnapshotAnchor'));
        const currentSelection = String(UserSession.get('statsSnapshotAnchorSelection') || '');
        const hasSelection = !forceLatest && currentSelection && Array.from(select.options).some(opt => opt.value === currentSelection);

        if (hasSelection) {
            select.value = currentSelection;
        } else if (!forceLatest && Number.isFinite(currentAnchor)) {
            const match = Array.from(select.options).find(opt => {
                const [ rawTs ] = String(opt.value || '').split('|');
                return Number(rawTs) === currentAnchor;
            });
            if (match) {
                select.value = match.value;
            }
        }

        // Update snapshot anchor to first option if needed
        if (allOptions.length > 0) {
            const first = allOptions[0];
            if (!select.value || forceLatest) {
                select.value = buildValue(first);
            }

            const selectedOpt = select.selectedOptions?.[0];
            if (selectedOpt) {
                const [ ts ] = String(selectedOpt.value).split('|');
                const selectedTimestamp = Number(ts);
                const selectedMeta = {
                    scope: analysisWindow,
                    anchorType: String(selectedOpt.dataset.anchorType || 'calendar'),
                    isCurrentPeriod: String(selectedOpt.dataset.isCurrent || '') === 'true',
                    label: String(selectedOpt.textContent || '').trim(),
                };
                UserSession.set('statsSnapshotAnchor', selectedTimestamp);
                UserSession.set('statsSnapshotAnchorSelection', selectedOpt.value);
                UserSession.set('statsSnapshotAnchorMeta', selectedMeta);

                if (forceLatest || !hasSelection) {
                    // Emit event for card to reload
                    this.emit({
                        type: 'hs-snapshot-changed',
                        timestamp: selectedTimestamp,
                        analysisWindow,
                        ...selectedMeta,
                    });
                }
            }
        }

        // Show/hide selector based on whether options exist
        selectorEl.style.display = allOptions.length > 0 ? 'flex' : 'none';
    }

    buildGroupedOptions(analysisWindow, baseOptions = [], todayStartUtc = Date.now()) {
        const rolling = [];
        const calendar = [];

        if (analysisWindow === 'day') {
            rolling.push({
                label: 'Today',
                timestamp: todayStartUtc,
                anchorType: 'rolling',
                isCurrentPeriod: true,
            });
            baseOptions
                .filter((opt) => Number(opt?.timestamp) < Number(todayStartUtc))
                .forEach((opt) => calendar.push({
                    label: String(opt.label || ''),
                    timestamp: Number(opt.timestamp),
                    anchorType: 'calendar',
                    isCurrentPeriod: false,
                }));
            return { rolling, calendar };
        }

        if (analysisWindow === 'week') {
            rolling.push({ label: 'Last 7 Days', timestamp: Number(todayStartUtc), anchorType: 'rolling', isCurrentPeriod: true });
            baseOptions.forEach((opt, idx) => calendar.push({
                label: idx === 0 ? 'This Week' : String(opt.label || ''),
                timestamp: Number(opt.timestamp),
                anchorType: 'calendar',
                isCurrentPeriod: idx === 0,
            }));
            return { rolling, calendar };
        }

        if (analysisWindow === 'month') {
            rolling.push({ label: 'Last 30 Days', timestamp: Number(todayStartUtc), anchorType: 'rolling', isCurrentPeriod: true });
            baseOptions.forEach((opt, idx) => calendar.push({
                label: idx === 0 ? 'This Month' : String(opt.label || ''),
                timestamp: Number(opt.timestamp),
                anchorType: 'calendar',
                isCurrentPeriod: idx === 0,
            }));
            return { rolling, calendar };
        }

        if (analysisWindow === 'quarter') {
            rolling.push({ label: 'Last 90 Days', timestamp: Number(todayStartUtc), anchorType: 'rolling', isCurrentPeriod: true });
            baseOptions.forEach((opt, idx) => calendar.push({
                label: idx === 0 ? 'This Quarter' : String(opt.label || ''),
                timestamp: Number(opt.timestamp),
                anchorType: 'calendar',
                isCurrentPeriod: idx === 0,
            }));
            return { rolling, calendar };
        }

        if (analysisWindow === 'all') {
            rolling.push({
                label: 'All Time',
                timestamp: Number(todayStartUtc),
                anchorType: 'rolling',
                isCurrentPeriod: true,
            });
        }

        return { rolling, calendar };
    }

    reloadData() {
        return this.updateSnapshotOptions(this.currentAnalysisWindow, this.userLocaleWeekStart);
    }

    // Public method to update when time scope changes
    onTimeScopeChanged(timeScope) {
        Logger.debug("Time scope changed to: ", timeScope);

        if (timeScope !== this.currentAnalysisWindow) {
            this.currentAnalysisWindow = timeScope;
            this.updateSnapshotOptions(timeScope, this.userLocaleWeekStart, { forceLatest: true });
        }
    }
}

customElements.define("hs-snapshot-picker", HSSnapshotPicker);
